import { componentCls } from "@faire/slate/lib/tracking";
import React from "react";

import { getTabPanelProps } from "./utils";

type IProps<T extends string> = React.HTMLAttributes<HTMLElement> & {
  /**
   * **web**
   * Slate doesn't have any styles for the tab panel, so you can use this to apply your own styles.
   */
  className?: string;

  /**
   * **web**
   * Whether to force render the tab panel even if it is not selected.
   */
  forceRender?: boolean;

  /**
   * **web**
   * The value of the selected tab.
   */
  selectedTabValue: T;

  /**
   * **web**
   * The tab value that is associated with the panel.
   */
  tabValue: T;
};

/**
 * **web**
 * The panel that is associated with the tab. It renders a section element that can be customized.
 *
 * {@link https://www.notion.so/faire/Tab-2242efb5c25a801ca542d716289431df | Slate/TabPanel}
 */
export const TabPanel = <T extends string = string>({
  className,
  children,
  forceRender,
  selectedTabValue,
  tabValue,
  ...props
}: IProps<T>) => {
  if (!forceRender && selectedTabValue !== tabValue) {
    return null;
  }

  return (
    <section
      {...props}
      {...getTabPanelProps({
        tabValue,
        selectedTabValue,
        className: componentCls(className),
      })}
      data-component-name="Slate/TabPanel"
    >
      {children}
    </section>
  );
};
